import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExternalLink, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * Pre-redirect dialog shown before sending the user out to Stripe Connect
 * onboarding. Explains what they're about to see and gives clear guidance
 * on the trickier "Industry / Business" question Stripe asks.
 *
 * Reused by:
 *   - DriverPayoutSetup (driver lifts)
 *   - EventDetailsPage  (organiser ticket sales)
 *   - CreateEventPage   (organiser ticket sales)
 */
const StripeOnboardingConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  audience = 'driver', // 'driver' | 'organiser'
}) => {
  const isOrganiser = audience === 'organiser';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border/50 sm:max-w-md" data-testid="stripe-onboard-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Heading to Stripe
          </DialogTitle>
          <DialogDescription className="text-left pt-2">
            We use Stripe — the same payments system behind Uber, Deliveroo and Amazon — to pay you securely. You&apos;ll be redirected in a moment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Industry guidance */}
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2">
            <p className="text-sm font-semibold text-amber-500">
              When Stripe asks for your <span className="underline decoration-dotted">Industry</span>:
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Pick <span className="font-bold text-amber-400">&ldquo;Membership organisations&rdquo;</span> (or <span className="font-bold text-amber-400">&ldquo;Sports &amp; recreation clubs&rdquo;</span> if shown).
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Don&apos;t pick &ldquo;Clothing&rdquo;, &ldquo;Transportation&rdquo; or &ldquo;Ride sharing&rdquo; — they trigger heavier compliance checks that don&apos;t fit our community-platform model.
            </p>
          </div>

          {/* Website URL guidance */}
          <div className="p-4 rounded-xl bg-card border border-border/50 space-y-2">
            <p className="text-sm font-semibold">If asked for your website:</p>
            <p className="text-sm font-mono bg-muted/40 rounded px-2 py-1 inline-block">https://rollm8s.com</p>
          </div>

          {/* What you'll need */}
          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-2">
            <p className="text-sm font-semibold text-primary">Have these ready (~2 mins):</p>
            <div className="space-y-1.5 text-xs text-muted-foreground pl-1">
              <p className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                <span>Your name &amp; date of birth</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                <span>UK bank details (sort code &amp; account number)</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                <span>A photo of your ID — Stripe handles verification</span>
              </p>
              {isOrganiser && (
                <p className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                  <span>A short description of your event(s)</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="w-full rounded-full bg-primary hover:bg-primary/90"
            data-testid="stripe-onboard-confirm-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <ExternalLink className="w-4 h-4 mr-2" />
            )}
            Continue to Stripe
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full rounded-full text-muted-foreground"
            data-testid="stripe-onboard-cancel-btn"
          >
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StripeOnboardingConfirmDialog;
